import React from "react";
import PropTypes from "prop-types";
import * as PrimitiveProgress from "@radix-ui/react-progress";

export default function CircularProgress({
  value = 0,
  minValue = 0,
  maxValue = 100,
  size = 0,
  offset = 0,
  thickness = 6,
  children,
}) {
  const gradientId = `gradient-stroke-${React.useId()}`;

  // Ensure all values are non-negative
  const nonNegativeMinValue = Math.max(0, minValue);
  const nonNegativeMaxValue = Math.max(0, maxValue);
  const nonNegativeValue = Math.max(0, value);

  const validMinValue =
    nonNegativeMinValue >= nonNegativeMaxValue ? 0 : nonNegativeMinValue; // fallback to default minValue
  const validMaxValue =
    nonNegativeMaxValue <= nonNegativeMinValue ? 100 : nonNegativeMaxValue; // fallback to default maxValue

  // Clamp value between validMinValue and validMaxValue
  const clampedValue = Math.min(
    Math.max(nonNegativeValue, validMinValue),
    validMaxValue,
  );

  const circleSize = size + thickness * 2 + offset;

  // Calculate progress within a valid range
  const percentage =
    ((clampedValue - validMinValue) / (validMaxValue - validMinValue)) * 100;
  const radius = (circleSize - thickness) / 2;
  const circumference = 2 * Math.PI * radius;
  const progressOffset = circumference - (percentage / 100) * circumference;

  return (
    <PrimitiveProgress.Root
      value={clampedValue}
      max={validMaxValue}
      className="relative flex items-center justify-center"
    >
      {circleSize > 0 && (
        <PrimitiveProgress.Indicator asChild={true}>
          <svg width={circleSize} height={circleSize}>
            <defs>
              <radialGradient
                id={gradientId}
                cx="0"
                cy="0"
                r="1"
                gradientUnits="userSpaceOnUse"
                gradientTransform={`translate(${circleSize / 2} ${
                  circleSize / 2
                }) rotate(-90) scale(${radius * 1.5} ${radius / 2.5})`}
              >
                <stop offset="0.105" stopColor="#9258FF" />
                <stop offset="1" stopColor="#5D15E3" />
              </radialGradient>
            </defs>

            <circle
              cx={circleSize / 2}
              cy={circleSize / 2}
              r={radius}
              stroke={`url(#${gradientId})`}
              strokeWidth={thickness}
              fill="none"
              strokeDasharray={circumference}
              strokeDashoffset={progressOffset}
              strokeLinecap="round"
              transform={`rotate(-90 ${circleSize / 2} ${circleSize / 2})`}
            />
          </svg>
        </PrimitiveProgress.Indicator>
      )}

      {children && (
        <div className="absolute flex items-center justify-center">
          {children}
        </div>
      )}
    </PrimitiveProgress.Root>
  );
}

CircularProgress.propTypes = {
  children: PropTypes.node,
  value: PropTypes.number,
  minValue: PropTypes.number,
  maxValue: PropTypes.number,
  thickness: PropTypes.number,
  size: PropTypes.number,
  offset: PropTypes.number,
};
